import * as React from "react";

import { formsApi } from "@/api/forms";
import { Button } from "@/components/ui/button";
import { useFormBuilderStore } from "@/stores/formBuilder";
import { useUIStore } from "@/stores/ui";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Gọi sau khi publish thành công để refetch parent. */
  onPublished: () => void | Promise<void>;
}

/**
 * PublishDialog — modal lightweight (không dùng Radix Dialog để giảm dep).
 * Hiển thị change_notes form, validate gọi /preview trước, rồi publish.
 */
export function PublishDialog({ open, onClose, onPublished }: Props): JSX.Element | null {
  const formCode = useFormBuilderStore((s) => s.formCode);
  const versionInfo = useFormBuilderStore((s) => s.versionInfo);
  const isDirty = useFormBuilderStore((s) => s.isDirty);
  const pushToast = useUIStore((s) => s.pushToast);
  const [busy, setBusy] = React.useState(false);

  if (!open || !formCode || !versionInfo) return null;

  const handlePublish = async (): Promise<void> => {
    if (isDirty) {
      pushToast({
        variant: "warning",
        title: "Save Draft trước khi publish",
      });
      return;
    }
    setBusy(true);
    try {
      await formsApi.publish(formCode, versionInfo.version);
      pushToast({
        variant: "success",
        title: `Đã publish ${formCode} v${versionInfo.version}`,
      });
      await onPublished();
      onClose();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Publish thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-background rounded-lg border shadow-lg max-w-md w-full p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold">Publish form</h2>
        <div className="space-y-2 text-sm">
          <p>
            Form: <code className="bg-muted px-1.5 py-0.5 rounded">{formCode}</code>
          </p>
          <p>
            Version sắp publish: <span className="font-mono font-semibold">v{versionInfo.version}</span>
          </p>
          <p className="text-muted-foreground text-xs">
            Sau khi publish, version đang published cũ (nếu có) sẽ bị archive. User
            sẽ thấy schema mới ngay lập tức (không cache).
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Huỷ
          </Button>
          <Button onClick={() => void handlePublish()} disabled={busy}>
            {busy ? "Đang publish..." : `Publish v${versionInfo.version}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
